import React, { createContext, useContext, useState } from 'react';
import { getLocale, setLocale as setParaglideLocale, locales } from '@/paraglide/runtime';

interface LanguageContextType {
    locale: string;
    setLocale: (locale: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
    // 1. Initial State Resolution Logic
    const getInitialLocale = () => {
        // A. Check localStorage first (user's explicit previous choice)
        const saved = localStorage.getItem('ssc_user_locale');
        if (saved && locales.includes(saved as any)) return saved;

        // B. Detect from Browser/Phone settings
        try {
            const browserLangs = navigator.languages || [navigator.language];
            for (const lang of browserLangs) {
                const shortLang = lang.split('-')[0].toLowerCase();
                // Special check for Chinese dialects to ensure they map to 'zh'
                const checkLang = shortLang === 'zh' ? 'zh' : shortLang;
                if (locales.includes(checkLang as any)) {
                    return checkLang;
                }
            }
        } catch (e) {
            console.error("Browser language detection failed", e);
        }

        // C. Default fallback
        return getLocale();
    };

    const [locale, setLocaleState] = useState(getInitialLocale());

    // 2. Wrap setLocale to include side effects
    const setLocale = (newLocale: string) => {
        // Save choice
        localStorage.setItem('ssc_user_locale', newLocale);

        // Update Paraglide without reload
        setParaglideLocale(newLocale as any, { reload: false });

        // Update React state
        setLocaleState(newLocale as any);

        // Update HTML lang attribute
        document.documentElement.lang = newLocale;

        // Update direction (RTL support)
        const isRtl = ['ar'].includes(newLocale);
        document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    };

    // 3. Effect to ensure initial document properties align
    React.useEffect(() => {
        document.documentElement.lang = locale;
        const isRtl = ['ar'].includes(locale);
        document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
        // Ensure paraglide is in sync with detected locale
        setParaglideLocale(locale as any, { reload: false });
    }, [locale]);

    return (
        <LanguageContext.Provider value={{ locale, setLocale }}>
            <div className="contents">
                {children}
            </div>
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        // Fallback for cases where context is not yet available, to prevent black screen
        return {
            locale: getLocale(),
            setLocale: (l: string) => { console.warn("setLocale called outside LanguageProvider", l); }
        };
    }
    return context;
}
